import React, { useState, useEffect } from 'react';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { productService } from '../services/productService';
import platformService from '../services/platformService';
import { toast } from 'react-hot-toast';
import { countryOptions, getCountryCode, getCountryName } from '../utils/countries';

const ProductsScreen = () => {
    const [products, setProducts] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [salesInfo, setSalesInfo] = useState(null);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    const [bulkDeleteResult, setBulkDeleteResult] = useState(null);
    
    // Estados para la edición masiva
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [bulkEditFormData, setBulkEditFormData] = useState({
        country: '',
        city: ''
    });
    const [bulkEditLoading, setBulkEditLoading] = useState(false);
    const [bulkEditResult, setBulkEditResult] = useState(null);
    
    // Filtros y paginación
    const [filters, setFilters] = useState({
        search: '',
        platform: '',
        country: '',
        city: ''
    });
    const [tempFilters, setTempFilters] = useState({
        search: '',
        platform: '',
        country: '',
        city: ''
    });
    
    // Paginación
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });
    
    // Opciones de límite de paginación
    const limitOptions = [10, 20, 50, 100, 1000];
    
    // Ordenamiento
    const [sorting, setSorting] = useState({
        field: 'name',
        order: 'ASC'
    });

    // Estado para el formulario de edición
    const [formData, setFormData] = useState({
        name: '',
        url: '',
        notes: '',
        country: '',
        city: ''
    });

    const loadPlatforms = async () => {
        try {
            const data = await platformService.getActivePlatforms();
            setPlatforms(data || []);
        } catch (err) {
            console.error('Error al cargar plataformas:', err);
        }
    };

    const loadProducts = async () => {
        try {
            setLoading(true);
            
            // Preparar filtros para la API, incluyendo paginación y ordenamiento
            const apiFilters = {
                ...filters,
                page: pagination.page,
                limit: pagination.limit,
                sortField: sorting.field,
                sortOrder: sorting.order
            };
            
            // Manejar filtros especiales para país y ciudad
            if (filters.country === 'none') {
                apiFilters.country = 'none';
                console.log('Filtrando por país no definido');
            }
            
            if (filters.city === 'none') {
                apiFilters.city = 'none';
                console.log('Filtrando por ciudad no definida');
            }
            
            console.log('Enviando filtros a la API:', apiFilters);
            
            const data = await productService.getProducts(apiFilters);
            const currentPageProducts = data.products || data || [];
            
            if (data.products) {
                // Si la API devuelve un objeto con paginación
                setProducts(currentPageProducts);
                setPagination({
                    page: data.page || 1,
                    limit: data.limit || 10,
                    total: data.total || 0,
                    pages: data.pages || 0
                });
            } else {
                // Si la API devuelve un array simple
                setProducts(currentPageProducts);
                setPagination(prev => ({
                    ...prev,
                    total: currentPageProducts.length,
                    pages: Math.ceil(currentPageProducts.length / prev.limit)
                }));
            }

            setSelectedProducts(prevSelected =>
                prevSelected.filter(id => currentPageProducts.some(product => product.id === id))
            );
        } catch (error) {
            console.error('Error al cargar productos:', error);
            toast.error('Error al cargar los productos: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProducts();
        loadPlatforms();
    }, [filters, pagination.page, pagination.limit, sorting]);

    const handleEditClick = (product) => {
        setSelectedProduct(product);
        setFormData({
            name: product.name || '',
            url: product.url || '',
            notes: product.notes || '',
            country: getCountryCode(product.country) || '',
            city: product.city || ''
        });
        setShowModal(true);
    };

    const handleDeleteClick = async (product) => {
        try {
            setProductToDelete(product);
            const salesData = await productService.checkProductSales(product.id);
            setSalesInfo(salesData);
            setShowDeleteModal(true);
        } catch (error) {
            console.error('Error al verificar ventas:', error);
            toast.error('Error al verificar las ventas asociadas');
        }
    };

    const handleDeleteConfirm = async (force = false) => {
        try {
            if (force) {
                await productService.deleteProductWithSales(productToDelete.id);
                toast.success('Producto y ventas asociadas eliminados correctamente');
            } else {
                await productService.deleteProduct(productToDelete.id);
                toast.success('Producto eliminado correctamente');
            }
            setShowDeleteModal(false);
            setProductToDelete(null);
            setSalesInfo(null);
            await loadProducts();
        } catch (error) {
            console.error('Error al eliminar producto:', error);
            toast.error('Error al eliminar el producto: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedProduct(null);
        setFormData({ name: '', url: '', notes: '', country: '', city: '' });
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setProductToDelete(null);
        setSalesInfo(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'country' ? { city: '' } : {})
        }));
    };

    // Manejar cambios en los filtros temporales
    const handleTempFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({
            ...prev,
            [name]: value,
            // Si cambia el país, resetear ciudad a vacío
            ...(name === 'country' ? { city: '' } : {})
        }));
    };
    
    // Manejar cambio de país específicamente
    const handleCountryChange = (e) => {
        const selectedCountry = e.target.value;
        console.log('País seleccionado:', selectedCountry);
        
        // Actualizar el país y resetear la ciudad
        setTempFilters(prev => ({
            ...prev,
            country: selectedCountry,
            city: '' // Resetear ciudad cuando cambia el país
        }));
    };

    // Aplicar los filtros temporales
    const applyFilters = () => {
        setFilters(tempFilters);
        // Volver a la primera página al aplicar nuevos filtros
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Limpiar filtros
    const clearFilters = () => {
        const emptyFilters = {
            search: '',
            platform: '',
            country: '',
            city: ''
        };
        setTempFilters(emptyFilters);
        setFilters(emptyFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    };
    
    // Manejar cambios de página
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= pagination.pages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };
    
    // Manejar cambios en el límite de paginación
    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setPagination(prev => ({ 
            ...prev, 
            limit: newLimit,
            page: 1 // Volver a la primera página al cambiar el límite
        }));
    };
    
    // Manejar orden de columnas
    const handleSort = (field) => {
        setSorting(prev => {
            if (prev.field === field) {
                // Si es el mismo campo, alternar el orden
                return { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' };
            } else {
                // Si es un campo nuevo, ordenar ascendentemente por defecto
                return { field, order: 'ASC' };
            }
        });
    };
    
    // Renderizar ícono de ordenamiento
    const renderSortIcon = (field) => {
        if (sorting.field !== field) {
            return <span className="text-gray-400">⇅</span>;
        }
        return sorting.order === 'ASC' ? <span className="text-primary">↑</span> : <span className="text-primary">↓</span>;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            console.log('Actualizando producto:', selectedProduct.id, formData);
            await productService.updateProduct(selectedProduct.id, formData);
            toast.success('Producto actualizado correctamente');
            await loadProducts();
            handleCloseModal();
        } catch (error) {
            console.error('Error al actualizar producto:', error);
            toast.error('Error al actualizar el producto: ' + (error.response?.data?.message || error.message));
        }
    };

    // Función para mostrar el modal de confirmación de borrado masivo
    const handleShowBulkDeleteModal = () => {
        setShowBulkDeleteModal(true);
        setBulkDeleteResult(null);
    };

    // Función para cerrar el modal de borrado masivo
    const handleCloseBulkDeleteModal = () => {
        setShowBulkDeleteModal(false);
        setBulkDeleteResult(null);
    };

    // Función para ejecutar el borrado masivo de productos sin ventas
    const handleBulkDeleteConfirm = async () => {
        try {
            setBulkDeleteLoading(true);
            
            // Llamar al endpoint para borrar productos sin ventas
            const result = await productService.deleteProductsWithoutSales();
            
            setBulkDeleteResult(result);
            toast.success(`${result.deletedCount} productos sin ventas fueron eliminados correctamente`);
            
            // Recargar productos después de la eliminación
            await loadProducts();
        } catch (error) {
            console.error('Error al eliminar productos sin ventas:', error);
            toast.error('Error al eliminar productos: ' + (error.response?.data?.message || error.message));
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    // Función para manejar la selección/deselección de un producto
    const handleProductSelection = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    };

    // Función para seleccionar/deseleccionar todos los productos
    const handleSelectAllProducts = () => {
        if (selectedProducts.length === products.length) {
            // Si todos están seleccionados, deseleccionar todos
            setSelectedProducts([]);
        } else {
            // Seleccionar todos
            setSelectedProducts(products.map(product => product.id));
        }
    };

    // Función para mostrar el modal de edición masiva
    const handleShowBulkEditModal = () => {
        if (selectedProducts.length === 0) {
            toast.error('Debes seleccionar al menos un producto para editar');
            return;
        }
        
        setBulkEditFormData({
            country: '',
            city: ''
        });
        setBulkEditResult(null);
        setShowBulkEditModal(true);
    };

    // Función para cerrar el modal de edición masiva
    const handleCloseBulkEditModal = () => {
        setShowBulkEditModal(false);
        setBulkEditResult(null);
    };

    // Función para manejar cambios en el formulario de edición masiva
    const handleBulkEditInputChange = (e) => {
        const { name, value } = e.target;
        setBulkEditFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'country' ? { city: '' } : {})
        }));
    };

    // Función para realizar la edición masiva
    const handleBulkEditSubmit = async (e) => {
        e.preventDefault();
        
        // Verificar que al menos se ha proporcionado un campo para actualizar
        if (!bulkEditFormData.country && !bulkEditFormData.city) {
            toast.error('Debes proporcionar al menos país o ciudad para actualizar');
            return;
        }
        
        try {
            setBulkEditLoading(true);
            
            // Preparar datos para actualizar
            const updateData = {};
            if (bulkEditFormData.country) updateData.country = bulkEditFormData.country;
            if (bulkEditFormData.city) updateData.city = bulkEditFormData.city;
            
            // Llamar al servicio de actualización masiva
            const result = await productService.updateProductsBulk(selectedProducts, updateData);
            
            // Mostrar resultado
            setBulkEditResult(result);
            toast.success(`Se actualizaron ${result.updatedCount} productos correctamente`);
            
            // Recargar productos para ver los cambios
            await loadProducts();
            
            // Limpiar selección
            setSelectedProducts([]);
        } catch (error) {
            console.error('Error al actualizar productos:', error);
            toast.error('Error al actualizar productos: ' + (error.response?.data?.message || error.message));
        } finally {
            setBulkEditLoading(false);
        }
    };

    if (loading && products.length === 0) {
        return (
            <div className="flex justify-center p-8">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="page-header">
                <h1 className="page-title">Gestión de Productos</h1>
                <div className="page-actions">
                    <Link to="/products/new" className="btn btn-primary">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Nuevo Producto
                    </Link>
                    <Link to="/product-stats" className="btn btn-secondary">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Estadísticas por Productos
                    </Link>
                    <button 
                        onClick={handleShowBulkEditModal} 
                        className="btn btn-info"
                        title="Editar país/ciudad de múltiples productos"
                        disabled={selectedProducts.length === 0}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Editar Seleccionados ({selectedProducts.length})
                    </button>
                    <button 
                        onClick={handleShowBulkDeleteModal} 
                        className="btn btn-error"
                        title="Eliminar todos los productos que no tienen ventas asociadas"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Eliminar sin Ventas
                    </button>
                    <button onClick={loadProducts} className="btn btn-outline">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="card bg-base-100 shadow-xl mb-6">
                <div className="card-body">
                    <h2 className="card-title mb-4">Filtros</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Buscar</span>
                            </label>
                            <input
                                type="text"
                                name="search"
                                value={tempFilters.search}
                                onChange={handleTempFilterChange}
                                placeholder="Buscar por nombre..."
                                className="input input-bordered w-full"
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Plataforma</span>
                            </label>
                            <select
                                name="platform"
                                value={tempFilters.platform}
                                onChange={handleTempFilterChange}
                                className="select select-bordered w-full"
                            >
                                <option value="">Todas las plataformas</option>
                                {platforms.map(platform => (
                                    <option key={platform.id} value={platform.id}>
                                        {platform.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">País</span>
                            </label>
                            <select
                                name="country"
                                className="select select-bordered w-full"
                                value={tempFilters.country}
                                onChange={handleCountryChange}
                            >
                                <option value="">Todos los países</option>
                                <option value="none">No definido</option>
                                {/* Lista de países únicos extraídos de los productos */}
                                {Array.from(new Set(products.map(p => p.country).filter(Boolean))).sort().map(country => (
                                    <option key={country} value={country}>
                                        {getCountryName(country)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Ciudad</span>
                            </label>
                            <select
                                name="city"
                                className="select select-bordered w-full"
                                value={tempFilters.city}
                                onChange={handleTempFilterChange}
                                disabled={!tempFilters.country || tempFilters.country === 'none'}
                            >
                                <option value="">Todas las ciudades</option>
                                <option value="none">No definido</option>
                                {/* Lista de ciudades únicas extraídas de los productos, filtradas por país si es necesario */}
                                {Array.from(new Set(products
                                    .filter(p => !tempFilters.country || tempFilters.country === 'none' || p.country === tempFilters.country)
                                    .map(p => p.city)
                                    .filter(Boolean)))
                                    .sort()
                                    .map(city => (
                                        <option key={city} value={city}>
                                            {city}
                                        </option>
                                    ))
                                }
                            </select>
                        </div>  
                    </div>
                    <div className="flex justify-end mt-4 gap-2">
                        <button 
                            className="btn btn-ghost"
                            onClick={clearFilters}
                        >
                            Limpiar Filtros
                        </button>
                        <button 
                            className="btn btn-primary"
                            onClick={applyFilters}
                        >
                            Aplicar Filtros
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-base-100 shadow-xl rounded-box">
                {products.length === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-base-content/70">No hay productos disponibles</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table table-zebra w-full">
                            <thead>
                                <tr>
                                    <th>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                className="checkbox" 
                                                checked={selectedProducts.length === products.length && products.length > 0}
                                                onChange={handleSelectAllProducts}
                                            />
                                        </label>
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('id')}>
                                        ID {renderSortIcon('id')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('name')}>
                                        Nombre {renderSortIcon('name')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('platformId')}>
                                        Plataforma {renderSortIcon('platformId')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('country')}>
                                        País {renderSortIcon('country')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('city')}>
                                        Ciudad {renderSortIcon('city')}
                                    </th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4">
                                            No se encontraron productos.
                                        </td>
                                    </tr>
                                ) : (
                                    products.map(product => (
                                        <tr key={product.id}>
                                            <td>
                                                <label>
                                                    <input 
                                                        type="checkbox" 
                                                        className="checkbox" 
                                                        checked={selectedProducts.includes(product.id)}
                                                        onChange={() => handleProductSelection(product.id)}
                                                    />
                                                </label>
                                            </td>
                                            <td>
                                                <Link 
                                                    to={`/sales?product=${product.id}`}
                                                    className="link link-primary font-medium"
                                                    title="Ver ventas de este producto"
                                                >
                                                    {product.id}
                                                </Link>
                                            </td>
                                            <td>
                                                <div className="font-medium">
                                                    {product.name}
                                                </div>
                                                {product.url && (
                                                    <a href={product.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline">
                                                        Ver en web
                                                    </a>
                                                )}
                                            </td>
                                            <td>
                                                {platforms.find(p => p.id === product.platformId)?.name || 'Desconocida'}
                                            </td>
                                            <td>{getCountryName(product.country) || '-'}</td>
                                            <td>{product.city || '-'}</td>
                                            <td>
                                                <div className="table-actions">
                                                    <Link 
                                                        to={`/products/${product.id}`}
                                                        className="table-action-btn table-action-btn-info"
                                                        title="Ver detalles del producto"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                    </Link>
                                                    <button 
                                                        onClick={() => handleEditClick(product)}
                                                        className="table-action-btn table-action-btn-neutral"
                                                        title="Editar producto"
                                                    >
                                                        <FiEdit size={18} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteClick(product)}
                                                        className="table-action-btn table-action-btn-danger"
                                                        title="Eliminar producto"
                                                    >
                                                        <FiTrash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
                
            {/* Paginación */}
            {products.length > 0 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center gap-4">
                        <div className="text-sm">
                            Mostrando <span className="font-medium">{products.length}</span> de <span className="font-medium">{pagination.total}</span> resultados
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Mostrar:</span>
                            <select 
                                className="select select-bordered select-sm"
                                value={pagination.limit}
                                onChange={handleLimitChange}
                            >
                                {limitOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="join">
                        <button 
                            className="join-item btn btn-sm" 
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.page === 1}
                        >
                            «
                        </button>
                        <button 
                            className="join-item btn btn-sm" 
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page === 1}
                        >
                            ‹
                        </button>
                        
                        {/* Renderizar números de página */}
                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                            let pageNum;
                            if (pagination.pages <= 5) {
                                pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                                pageNum = i + 1;
                            } else if (pagination.page >= pagination.pages - 2) {
                                pageNum = pagination.pages - 4 + i;
                            } else {
                                pageNum = pagination.page - 2 + i;
                            }
                            
                            return (
                                <button
                                    key={pageNum}
                                    className={`join-item btn btn-sm ${pagination.page === pageNum ? 'btn-active' : ''}`}
                                    onClick={() => handlePageChange(pageNum)}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                        
                        <button 
                            className="join-item btn btn-sm" 
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page === pagination.pages}
                        >
                            ›
                        </button>
                        <button 
                            className="join-item btn btn-sm" 
                            onClick={() => handlePageChange(pagination.pages)}
                            disabled={pagination.page === pagination.pages}
                        >
                            »
                        </button>
                    </div>
                </div>
            )}

            {/* Modal de edición */}
            {showModal && selectedProduct && (
                <div className="modal modal-open">
                    <div className="modal-box w-11/12 max-w-2xl">
                        <h3 className="font-bold text-lg">Editar Producto: {selectedProduct.name}</h3>
                        <form onSubmit={handleSubmit} className="py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Nombre</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        name="name" 
                                        value={formData.name} 
                                        onChange={handleInputChange}
                                        className="input input-bordered w-full" 
                                        placeholder="Nombre del producto"
                                    />
                                </div>
                                
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">País</span>
                                    </label>
                                    <select
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        className="select select-bordered w-full"
                                    >
                                        <option value="">Sin país</option>
                                        {countryOptions.map(({ code, name }) => (
                                            <option key={code} value={code}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Ciudad</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        name="city" 
                                        value={formData.city} 
                                        onChange={handleInputChange}
                                        className="input input-bordered w-full" 
                                        placeholder="Madrid, Roma, etc."
                                    />
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">URL</span>
                                    </label>
                                    <input 
                                        type="url" 
                                        name="url" 
                                        value={formData.url} 
                                        onChange={handleInputChange}
                                        className="input input-bordered w-full" 
                                        placeholder="https://ejemplo.com/producto"
                                    />
                                </div>

                                <div className="form-control md:col-span-2">
                                    <label className="label">
                                        <span className="label-text">Notas</span>
                                    </label>
                                    <textarea 
                                        name="notes" 
                                        value={formData.notes} 
                                        onChange={handleInputChange}
                                        className="textarea textarea-bordered w-full h-24" 
                                        placeholder="Notas adicionales sobre el producto"
                                    ></textarea>
                                </div>
                            </div>

                            <div className="modal-action">
                                <button type="button" className="btn btn-ghost" onClick={handleCloseModal}>Cancelar</button>
                                <button type="submit" className="btn btn-primary">Guardar Cambios</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de eliminación */}
            {showDeleteModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="text-2xl font-bold mb-4">
                            Eliminar Producto
                        </h3>
                        <div className="space-y-4">
                            <p>¿Estás seguro de que deseas eliminar el producto <span className="font-medium">{productToDelete?.name}</span>?</p>
                            
                            {salesInfo && salesInfo.count > 0 && (
                                <div className="alert alert-warning">
                                    <p>Este producto tiene {salesInfo.count} ventas asociadas.</p>
                                    <Link 
                                        to={`/sales?product=${productToDelete?.id}`}
                                        className="link link-primary"
                                        target="_blank"
                                    >
                                        Ver ventas asociadas
                                    </Link>
                                    <p className="mt-2">Si continúas, todas las ventas asociadas serán eliminadas también.</p>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button 
                                className="btn btn-ghost" 
                                onClick={handleCloseDeleteModal}
                            >
                                Cancelar
                            </button>
                            {salesInfo && salesInfo.count > 0 ? (
                                <button 
                                    className="btn btn-error"
                                    onClick={() => handleDeleteConfirm(true)}
                                >
                                    Eliminar producto y ventas
                                </button>
                            ) : (
                                <button 
                                    className="btn btn-error"
                                    onClick={() => handleDeleteConfirm(false)}
                                >
                                    Eliminar producto
                                </button>
                            )}
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={handleCloseDeleteModal}>close</button>
                    </form>
                </div>
            )}

            {/* Modal de confirmación para eliminación masiva */}
            {showBulkDeleteModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="text-2xl font-bold mb-4">
                            Eliminar Productos sin Ventas
                        </h3>
                        
                        {!bulkDeleteResult ? (
                            <div className="space-y-4">
                                <p>¿Estás seguro de que deseas eliminar <span className="font-medium">todos los productos</span> que no tienen ventas asociadas?</p>
                                <p>Esta acción no se puede deshacer.</p>
                                
                                <div className="alert alert-info">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>Se eliminarán solo los productos que no tienen ventas asociadas.</span>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="alert alert-success">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>Se han eliminado {bulkDeleteResult.deletedCount} productos sin ventas correctamente.</span>
                                </div>
                            </div>
                        )}

                        <div className="modal-action">
                            <button 
                                className="btn btn-ghost" 
                                onClick={handleCloseBulkDeleteModal}
                            >
                                {bulkDeleteResult ? 'Cerrar' : 'Cancelar'}
                            </button>
                            {!bulkDeleteResult && (
                                <button 
                                    className="btn btn-error"
                                    onClick={handleBulkDeleteConfirm}
                                    disabled={bulkDeleteLoading}
                                >
                                    {bulkDeleteLoading ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Eliminando...
                                        </>
                                    ) : (
                                        'Eliminar todos'
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para la edición masiva */}
            {showBulkEditModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="text-2xl font-bold mb-4">
                            Edición Masiva de Productos
                        </h3>
                        
                        {!bulkEditResult ? (
                            <form onSubmit={handleBulkEditSubmit} className="space-y-4">
                                <div className="alert alert-info mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <p>Se actualizarán <span className="font-medium">{selectedProducts.length} productos</span>.</p>
                                        <p>Los campos vacíos no serán modificados.</p>
                                    </div>
                                </div>
                                
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">País</span>
                                    </label>
                                    <select
                                        name="country"
                                        value={bulkEditFormData.country}
                                        onChange={handleBulkEditInputChange}
                                        className="select select-bordered w-full"
                                    >
                                        <option value="">No cambiar</option>
                                        {countryOptions.map(({ code, name }) => (
                                            <option key={code} value={code}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Ciudad</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={bulkEditFormData.city}
                                        onChange={handleBulkEditInputChange}
                                        placeholder="Ciudad para todos los productos seleccionados"
                                        className="input input-bordered w-full"
                                    />
                                </div>
                                
                                <div className="modal-action">
                                    <button 
                                        type="button"
                                        className="btn btn-ghost" 
                                        onClick={handleCloseBulkEditModal}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={bulkEditLoading || (!bulkEditFormData.country && !bulkEditFormData.city)}
                                    >
                                        {bulkEditLoading ? (
                                            <>
                                                <span className="loading loading-spinner loading-sm"></span>
                                                Actualizando...
                                            </>
                                        ) : (
                                            'Actualizar Productos'
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="space-y-4">
                                <div className="alert alert-success">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>
                                        Se han actualizado {bulkEditResult.updatedCount} productos correctamente.
                                    </span>
                                </div>
                                
                                <div className="modal-action">
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={handleCloseBulkEditModal}
                                    >
                                        Cerrar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductsScreen; 
